import { Seo } from "@/components/web/Seo";
import { FadeUp } from "@/components/web/Reveal";
import { Mail } from "lucide-react";

const SUPPORT_EMAIL = "support@linkexpln.com";
const MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
  "Delete my LinkExpln account"
)}&body=${encodeURIComponent(
  `Hello LinkExpln team,

I would like to request the deletion of my LinkExpln account and all associated personal data.

Google account email used to sign in: [please enter the email address you use to sign in to the app]

I confirm that I want my LinkExpln account permanently deleted.

Thank you.`
)}`;

const DELETED_DATA = [
  "Account / profile information",
  "Google sign-in details associated with the account (name, email, account identifier)",
  "Scan history and saved scan results associated with the account",
  "Active authentication / session information",
  "Other account-associated data not required to be retained for legitimate legal, security, fraud-prevention or compliance purposes",
];

const STEPS = [
  {
    title: "1. Email us from your Google account email",
    body: `Send an account deletion request to ${SUPPORT_EMAIL}, preferably from the same Google account email address you use to sign in to the LinkExpln app. This lets us verify the request belongs to you.`,
  },
  {
    title: "2. Identify your account",
    body: "Include the Google account email address you use to sign in to LinkExpln in the request so we can locate your account. If you are writing from a different email address, clearly state which Google account email your LinkExpln account uses.",
  },
  {
    title: "3. State your request clearly",
    body: 'Your request should clearly say "Delete my LinkExpln account". Using the button below prefills this for you.',
  },
  {
    title: "4. Review and processing",
    body: "We will review and process your request. Once verified, your account and its associated data will be deleted, subject to the limited retention described below.",
  },
];

export default function DeleteAccount() {
  return (
    <>
      <Seo
        title="Delete Your LinkExpln Account"
        description="Request deletion of your LinkExpln account and associated data."
      />
      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">Account</p>
          <h1
            data-testid="delete-account-heading"
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl"
          >
            Delete your LinkExpln account
          </h1>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-2xl">
            LinkExpln™ users can request deletion of their account and the
            personal data associated with it at any time. This page explains
            how to submit a deletion request and what data is deleted.
          </p>
          <a
            href={MAILTO}
            data-testid="request-account-deletion-button"
            className="mt-8 inline-flex items-center gap-2 btn-brand text-black font-heading font-bold text-sm px-6 py-3 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
          >
            <Mail size={16} strokeWidth={2.5} /> Request account deletion
          </a>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-14">
          <FadeUp y={20}>
            <h2 className="font-heading font-bold text-xl text-white mb-4">
              How to request deletion
            </h2>
            <div className="space-y-8">
              {STEPS.map((s) => (
                <div key={s.title}>
                  <h3 className="font-heading font-bold text-base text-white mb-2">
                    {s.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp y={20}>
            <h2 className="font-heading font-bold text-xl text-white mb-4">
              What data is deleted
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-4">
              When your deletion request is processed, the following data
              associated with your account may be deleted:
            </p>
            <ul className="space-y-3">
              {DELETED_DATA.map((item) => (
                <li key={item} className="flex gap-3 text-zinc-400 leading-relaxed">
                  <span className="text-[#00C5FE] mt-1 shrink-0">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </FadeUp>

          <FadeUp y={20}>
            <h2 className="font-heading font-bold text-xl text-white mb-4">
              Limited retention
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Some information may need to be retained for a limited period
              when required for legitimate legal, security, fraud-prevention
              or compliance purposes — for example, records needed to prevent
              abuse of the service. Any retained information is kept only as
              long as necessary for those purposes. See our{" "}
              <a href="/privacy" className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200">
                Privacy Policy
              </a>{" "}
              for more detail.
            </p>
          </FadeUp>

          <FadeUp y={20}>
            <h2 className="font-heading font-bold text-xl text-white mb-4">
              Contact
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Send your deletion request, or any questions about it, to{" "}
              <a
                href={MAILTO}
                data-testid="delete-account-email-link"
                className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
